import { ref, onMounted, onBeforeUnmount, type Ref } from 'vue';
import type { PagiflowInstance, PagiflowOptions } from './types';

type PagiflowCtor = (
  selector: string | HTMLElement,
  opts?: PagiflowOptions
) => PagiflowInstance;

function getPagiflow(): PagiflowCtor | null {
  if (typeof window !== 'undefined' && (window as any).Pagiflow) {
    return (window as any).Pagiflow as PagiflowCtor;
  }
  try {
    const mod = require('pagiflow');
    return (mod.default ?? mod) as PagiflowCtor;
  } catch {
    return null;
  }
}

function initPagiflowFromElement(
  ctor: PagiflowCtor,
  el: HTMLElement,
  opts: PagiflowOptions
): PagiflowInstance | null {
  const attrValue = `pf-${Math.random().toString(36).slice(2)}`;
  const selector = `[data-pagiflow-vue-id="${attrValue}"]`;
  el.setAttribute('data-pagiflow-vue-id', attrValue);

  const bySelector = ctor(selector, opts) as PagiflowInstance | null;
  if (!bySelector) {
    el.removeAttribute('data-pagiflow-vue-id');
    return null;
  }
  return bySelector;
}

export interface UsePagiflowReturn {
  sliderRef: Ref<HTMLElement | null>;
  instance: Ref<PagiflowInstance | null>;
  currentIndex: Ref<number>;
  goTo: (index: number, opts?: { silent?: boolean; instant?: boolean }) => void;
  next: () => void;
  prev: () => void;
  play: () => void;
  pause: () => void;
  togglePlayPause: () => void;
  setOptions: (opts: Partial<PagiflowOptions>, rebuild?: boolean) => void;
  reInit: (opts?: Partial<PagiflowOptions>) => void;
}

export function usePagiflow(options: PagiflowOptions = {}): UsePagiflowReturn {
  const sliderRef = ref<HTMLElement | null>(null);
  const instance = ref<PagiflowInstance | null>(null);
  const currentIndex = ref<number>(options.startIndex ?? 0);

  let resizeObserver: ResizeObserver | null = null;
  let raf1 = 0;
  let raf2 = 0;
  let timeoutId = 0;

  onMounted(() => {
    const el = sliderRef.value;
    if (!el) return;

    const existing = (el as any).pagiflowInstance as PagiflowInstance | undefined;
    if (existing && !existing.destroyed) {
      instance.value = existing;
      return;
    }

    const ctor = getPagiflow();
    if (!ctor) {
      console.error('[usePagiflow] Pagiflow core not found. Install pagiflow or load it via CDN.');
      return;
    }

    const inst = initPagiflowFromElement(ctor, el, options);
    if (!inst) return;

    const syncLayout = () => {
      const api = inst as PagiflowInstance & { update?: () => void };
      api.update?.();
    };

    raf1 = window.requestAnimationFrame(syncLayout);
    raf2 = window.requestAnimationFrame(syncLayout);
    timeoutId = window.setTimeout(syncLayout, 80);
    window.addEventListener('load', syncLayout, { once: true });

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(syncLayout);
      resizeObserver.observe(el);
    }

    inst.onSlideChange((idx) => {
      currentIndex.value = idx;
    });

    instance.value = inst;
  });

  onBeforeUnmount(() => {
    const el = sliderRef.value;
    window.cancelAnimationFrame(raf1);
    window.cancelAnimationFrame(raf2);
    window.clearTimeout(timeoutId);
    resizeObserver?.disconnect();

    if (instance.value && !instance.value.destroyed) {
      try {
        instance.value.destroy();
      } catch {
        // ignore unmount destroy errors
      }
    }

    if (el) delete (el as any).pagiflowInstance;
    instance.value = null;
  });

  const goTo = (index: number, opts?: { silent?: boolean; instant?: boolean }) => {
    instance.value?.goTo(index, opts);
  };

  const next = () => instance.value?.next();
  const prev = () => instance.value?.prev();
  const play = () => instance.value?.play();
  const pause = () => instance.value?.pause();
  const togglePlayPause = () => instance.value?.togglePlayPause();
  const setOptions = (opts: Partial<PagiflowOptions>, rebuild = false) =>
    instance.value?.setOptions(opts, rebuild);
  const reInit = (opts: Partial<PagiflowOptions> = {}) => instance.value?.reInit(opts);

  return {
    sliderRef,
    instance,
    currentIndex,
    goTo,
    next,
    prev,
    play,
    pause,
    togglePlayPause,
    setOptions,
    reInit,
  };
}
